/**
 * English string table. It is the default locale and ships inline with the main
 * bundle; every other locale lives in its own lazily loaded chunk.
 */
export const EN_STRINGS = {
  dialogAriaLabel: '{company} chat',
  onlineWithCompany: 'Online · {company}',
  settingsLink: 'Settings',
  sessions: 'Sessions',
  minimizeChat: 'Minimize',
  moreOptions: 'More options',
  home: 'Home',
  exportOptions: 'Export options',
  exportAsJson: 'Export as JSON',
  exportAsText: 'Export as Text',
  exportAsMarkdown: 'Export as Markdown',
  loading: 'Loading...',
  chatMessages: 'Chat messages',
  emptyState: 'Say hi to start the conversation.',
  scrollToLatest: 'Scroll to latest messages',
  latest: 'Latest',
  attachFiles: 'Attach files',
  startVoiceInput: 'Start voice input',
  startVideoChat: 'Start video chat',
  messagePlaceholder: 'Message...',
  messageInputAria: 'Message input',
  messageInputHelp: 'Press Enter to send. Press Shift and Enter for a new line.',
  sendMessage: 'Send message',
  stopGenerating: 'Stop generating',
  responseStopped: 'Stopped',
  sendFailed: 'Message not sent',
  retry: 'Retry',
  retrySend: 'Retry sending message',
  connectionOffline: "You're offline. We'll send your message when you're back online.",
  sendQueuedOffline: 'Waiting for connection. This will send when you’re back online.',
  removeAttachment: 'Remove attachment {name}',
  domainBlockedStatus: 'Chat is disabled for this domain.',
  messageLimitStatus: 'You have reached the message limit for this session.',
  offlineStatus: 'We are currently offline.',
  preChatRequiredError: 'Complete the pre-chat form to start chatting.',
  consentRequiredError: 'Please accept the consent to continue.',
  consentCheckboxLabel: 'Chat consent',
  consentDefaultText: 'By starting this chat, you agree to our terms.',
  consentLearnMore: 'Learn more',
  rateLimitedError: "You're sending too fast. Try again in {seconds}s.",
  openChat: 'Open chat',
  errorAnnouncement: 'Error: {message}',
  dropFilesToUpload: 'Drop files to upload',
  yourMessageAria: 'Your message',
  assistantMessageAria: 'Assistant message',
  sourcesHeading: 'Sources:',
  copyMessage: 'Copy message',
  goodResponse: 'Good response',
  thumbsUp: 'Thumbs up',
  notHelpful: 'Not helpful',
  thumbsDown: 'Thumbs down',
  feedbackCommentPlaceholder: 'What went wrong? (optional)',
  feedbackThanks: 'Thanks for your feedback.',
  agentTypingAria: 'Agent is typing',
  // Streaming cues spoken by the dedicated status region (see useStreamingAnnouncements)
  responseStarted: 'Response in progress.',
  responseProgress: 'Still responding…',
  responseComplete: 'Response complete.',
  responseCompleteLong: 'Response complete, {words} words.',
  responseIncludesProducts: 'Includes {products}.',
  runningTools: 'Running tools...',
  selectOption: 'Select an option',
  startChat: 'Start chat',
  submit: 'Submit',
  fieldRequired: '{field} is required.',
  sessionsTitle: 'Sessions',
  closeSessions: 'Close sessions',
  newSession: 'New session',
  sessionsTotal: '{count} total',
  noSessionsYet: 'No sessions yet',
  newConversation: 'New conversation',
  deleteSession: 'Delete session',
  confirmDelete: 'Confirm?',
  confirmDeleteAria: 'Confirm delete',
  devTelemetry: 'Dev telemetry',
  refresh: 'Refresh',
  loadingTelemetry: 'Loading telemetry...',
  noTelemetry: 'No telemetry available yet.',
  telemetryRequests: 'Requests',
  telemetryErrors: 'Errors',
  telemetryAvgLatency: 'Avg latency',
  telemetryLastLatency: 'Last latency',
  telemetryKbHitRate: 'KB hit rate',
  telemetryTokensAvg: 'Tokens (avg)',
  telemetryLastRequest: 'Last request: {provider} / {model} / {mode}',
  telemetryModeStream: 'stream',
  telemetryModeSync: 'sync',
  telemetryAttributes: 'Attributes',
  telemetryRules: 'Rules',
  telemetryExamples: 'Examples',
  telemetryKbDocs: 'KB docs',
  none: 'None',
  orderDetails: 'Order details',
  viewOrderHistory: 'View order history',
  hideOrderHistory: 'Hide order history',
  orderHistory: 'Order History',
  previous: 'Previous',
  next: 'Next',
  agentReasoning: 'Agent Reasoning',
  expandReasoning: 'Expand agent reasoning',
  collapseReasoning: 'Collapse agent reasoning',
  // Customer email capture
  emailAddressLabel: 'Email address',
  emailPlaceholder: 'you@example.com',
  emailInvalid: 'Please enter a valid email address.',
  emailHelper: 'Used for order, subscription, and support follow-up.',
  // Empty-state footer
  aiTermsNotice:
    "This business uses AI from StateSet to generate messages. By chatting you agree to StateSet's AI Terms.",
  aiTermsLearnMore: 'Learn More',
  // Tabs / record views
  contentViewsAria: 'Content views',
  viewModeAria: 'View mode',
  noRecordsYet: 'No {label} to show yet.',
  recordOverviewAria: 'Record overview',
  totalRecords: 'Total records',
  rolling30Days: 'Rolling 30 days',
  activeWorkflows: 'Active workflows',
  needAttention: 'Need attention',
  lastUpdate: 'Last update',
  latestCustomer: 'Latest: {name}',
  awaitingFirstUpdate: 'Awaiting first update',
  nextAction: 'Next:',
  productColumn: 'Product',
  statusColumn: 'Status',
  priceColumn: 'Price',
  stockColumn: 'Stock',
  trendColumn: 'Trend',
  notAvailable: 'N/A',
  avgOrder: 'Avg Order',
  rating: 'Rating',
  vsLastWeek: '{trend} vs last week',
  // Product cards
  productRecommendationsAria: 'Product recommendations',
  priceUnavailable: 'Price unavailable',
  ratingAria: '{rating} out of 5 stars',
  ratingWithReviewsAria: '{rating} out of 5 stars from {reviews}',
  noRatingAria: 'No rating or reviews available',
  noRatingsYet: 'No ratings yet',
  whyItMatches: 'Why it matches:',
  whyItMatchesFallback: 'Matches the category you asked to explore.',
  viewProductPageAria: 'View {title} product page',
  viewProduct: 'View product',
  productLinkUnavailable: 'Product link unavailable',
  shade: 'Shade',
  chooseShade: 'Choose a shade',
  variantForProductAria: '{label} for {title}',
  soldOut: 'Sold out',
  chooseShadeFirst: 'Choose a shade first',
  chooseOptionFirst: 'Choose an option first',
  outOfStock: 'Out of stock',
  productActionAria: '{action} - {title}',
  // Rich messages
  opensInNewTab: '{title} (opens in new tab)',
  cardImageAlt: 'Card image',
  // Error boundary
  somethingWentWrong: 'Something went wrong',
  unexpectedError: 'An unexpected error occurred.',
  unexpectedErrorRetry: 'An unexpected error occurred. Please try again.',
  tryAgain: 'Try again',
  // Send failures (user-facing; diagnostics go to the console)
  sendFailedGeneric: "We couldn't send your message. Please try again.",
  sendFailedNetwork: 'Failed to send. Please try again.',
  sendFailedRateLimited:
    'Failed to send. Too many requests right now. Please try again in a moment.',
  sendFailedServer: 'Failed to send. The chat service is temporarily unavailable.',
  sendFailedTimeout: 'The response took too long. Please try again.',
  replyReceivedFallback: 'Thanks! We received your message.',
  // Generic answers / cart confirmation
  yes: 'Yes',
  no: 'No',
  keepShopping: 'Keep shopping',
  discountsAndRewards: 'Discounts & Rewards',
  thatItem: 'that item',
  thisProduct: 'this product',
  cartAdded: 'Added {item} to your cart.',
  cartAddUnconfirmed:
    'We could not confirm whether {item} was added. Please check your bag before trying again to avoid adding it twice.',
  cartQuantity: 'Quantity: {count}.',
  cartDiscountApplied: 'Discount {code} applied.',
  cartUpdatedTotal: 'Updated total: {total}.',
  cartUpdatedSubtotal: 'Updated subtotal: {subtotal}.',
  cartCheckoutLink: 'Checkout: {url}',
  cartCheckoutFromCart: 'Checkout is available from your cart.',
  cartKeepShoppingHint: 'You can keep shopping and check out whenever you’re ready.',
  cartReadyForCheckout: 'Are you ready to go to checkout?',
  cartProductHelpPrompt: 'What would you like to know about your product?',
  cartDiscountHelpPrompt: 'How can I help with discounts and rewards?',
  cartAddFailed:
    'I could not add {item} to your cart from chat. Please open the product page to add it there.',
  mainMenu: 'Main Menu',
  complementaryHeadline: 'Complete your routine',
  complementaryText: 'These products pair well with {item} if you’d like to keep shopping.',
  cartHelpApplyQuestion: 'How do I apply {item}?',
  cartHelpIngredientsQuestion: 'What are the ingredients in {item}?',
  // Widget shell messages (idle timeout, email gate, handoff fallback)
  idlePrompt: 'I hope this information was useful. Is there anything else I can help you with?',
  sessionEnded: 'This chat session has ended. Feel free to start a new conversation anytime!',
  emailRequiredError: 'Please enter your email address before continuing.',
  handoffFallback:
    'Our AI assistant could not complete the handoff in chat. Please leave your email and a short message, and someone on our team will follow up by email.',
  // Messages sent on the visitor's behalf by generic business actions
  emailSignupMessage: 'Sign me up for updates.',
} as const;

/** Count-dependent messages, selected with `Intl.PluralRules` (see `formatCount`). */
export const EN_PLURALS = {
  openChatUnread: {
    one: 'Open chat ({count} unread message)',
    other: 'Open chat ({count} unread messages)',
  },
  liveRegionUnread: {
    one: 'You have {count} unread message.',
    other: 'You have {count} unread messages.',
  },
  sessionMessageCount: { one: '{count} message', other: '{count} messages' },
  reviewCount: { one: '{count} review', other: '{count} reviews' },
  productRecommendationCount: {
    one: '{count} product recommendation',
    other: '{count} product recommendations',
  },
  stockUnits: { one: '{count} unit', other: '{count} units' },
  showEarlierMessages: {
    one: 'Show earlier messages ({count} more)',
    other: 'Show earlier messages ({count} more)',
  },
} as const;
